import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import type { NodeHistoryVersionItem } from '@/components/Recipe/Drawer/NodeHistory/nodeHistory.types';
import type { Edge, Node } from '@/types/node';

/** Encode a path segment so `/` / `..` in caller-controlled IDs cannot traverse the URL. */

export interface NodeHistoryPage {
  items: NodeHistoryVersionItem[];
  nextCursor: string | null;
}

/** PATCH rename result: stored id and canonical title (trimmed; empty → null). */
export type RenameNodeHistoryVersionResponse = {
  id: string;
  title: string | null;
};

/** Read a prompt node's version history (newest first, cursor-paginated). */
export const getNodeHistoryApi = async ({
  workflowId,
  nodeId,
  cursor,
  limit,
}: {
  workflowId: string;
  nodeId: string;
  cursor?: string;
  limit?: number;
}): Promise<NodeHistoryPage> => {
  // Use the `recipes` alias, not `workflows`: the Cloudflare WAF blocks `/v1/workflows/*` (Skyvern
  // CVE-2025-49619 rule). Same controller, WAF-clean path. See docs/prompt-node-history/.
  const res = await safeAxios.get<NodeHistoryPage>(
    API_ROUTES.recipes.nodeHistoryByNode({ recipeId: workflowId, nodeId }),
    {
      params: { cursor, limit },
    },
  );
  return res.data;
};

/** Prompt-node ids with visible history for this workflow (shared menu gate). */
export const getNodeHistoryNodeIdsApi = async ({ workflowId }: { workflowId: string }): Promise<string[]> => {
  const res = await safeAxios.get<{ nodeIds: string[] }>(
    API_ROUTES.recipes.nodeHistoryNodeIds({ recipeId: workflowId }),
  );
  return res.data.nodeIds;
};

export interface CaptureNodeHistoryRequest {
  workflowId: string;
  batchId: string;
  nodes: Node[];
  edges: Edge[];
  modelNodeIds: string[];
  /** Requested executions in this batch (`numberOfRuns` from execute). Defaults to 1 server-side. */
  numberOfRuns?: number;
}

/** The `node.data` fields the server's version resolver reads; everything else is dropped. */
const CAPTURE_DATA_FIELDS = [
  'prompt', // promptV3 template
  'additionalPrompt', // prompt_concat template
  'value', // string/text template + number/list value
  'inputNodes', // wiring (variable → source, + cached resolved `.string`)
  'iteratorInput', // iterator config (incl. runMode)
  'isIterator', // iterator detection flag
  'selectedIndex', // iterator variant → selected value
  'options', // list-selector values
  'params', // list-selector values (params.options)
  'files', // media-iterator values (files.data.value)
  'inputNode', // text-iterator values (inputNode.stringArray)
  'input', // resolved per-handle values / variant merge target
] as const;

/** Strip the capture graph to resolver-only fields, keeping the payload small enough to pass the WAF. */
const sanitizeCaptureNodes = (nodes: Node[]): Node[] =>
  nodes.map((node) => {
    // Drop model-node `data` entirely: the resolver locates models via `modelNodeIds` + `isModel`,
    // and their `data` holds the heavy resolved-prompt prose that trips the WAF.
    if (node.isModel) {
      return { id: node.id, type: node.type, isModel: node.isModel, data: {} } as unknown as Node;
    }
    const data = (node.data ?? {}) as unknown as Record<string, unknown>;
    const picked: Record<string, unknown> = {};
    for (const key of CAPTURE_DATA_FIELDS) {
      if (key in data) picked[key] = data[key];
    }
    return { id: node.id, type: node.type, isModel: node.isModel, data: picked } as unknown as Node;
  });

const sanitizeCaptureEdges = (edges: Edge[]): Edge[] =>
  edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
  }));

/**
 * Fire-and-forget capture of prompt-node version history for an executed batch. The server resolves
 * versions from the submitted graph; failures are swallowed so capture never affects the run.
 */
export const captureNodeHistoryApi = async ({ workflowId, ...body }: CaptureNodeHistoryRequest): Promise<void> => {
  await safeAxios.post(
    API_ROUTES.recipes.nodeHistory({ recipeId: workflowId }),
    { ...body, nodes: sanitizeCaptureNodes(body.nodes), edges: sanitizeCaptureEdges(body.edges) },
    { 'axios-retry': { retries: 0 } },
  );
};

/** Soft-clear a node's version history (server stamps deletedAt + seals live head; log untouched). */
export const clearNodeHistoryApi = async ({
  workflowId,
  nodeId,
}: {
  workflowId: string;
  nodeId: string;
}): Promise<void> => {
  await safeAxios.post(API_ROUTES.recipes.nodeHistoryClear({ recipeId: workflowId, nodeId }), undefined, {
    'axios-retry': { retries: 0 },
  });
};

/** Set or clear a version's custom display title. Null/empty clears it (preview fallback). */
export const renameNodeHistoryVersionApi = async ({
  workflowId,
  nodeId,
  versionId,
  title,
}: {
  workflowId: string;
  nodeId: string;
  versionId: string;
  title: string | null;
}): Promise<RenameNodeHistoryVersionResponse> => {
  const res = await safeAxios.patch<RenameNodeHistoryVersionResponse>(
    API_ROUTES.recipes.nodeHistoryVersion({ recipeId: workflowId, nodeId, versionId }),
    { title },
    { 'axios-retry': { retries: 0 } },
  );
  return res.data;
};
